import type { LocaleCode } from "@guestpilot/i18n";

/** Dates, numbers and currency are formatted with `Intl` for the user's locale (04 §11); dates in the tenant time zone. */
export function formatDate(iso: string | null | undefined, locale: LocaleCode, timeZone?: string): string {
  if (!iso) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00Z`) : new Date(iso);
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    ...(timeZone && !/^\d{4}-\d{2}-\d{2}$/.test(iso) ? { timeZone } : { timeZone: "UTC" }),
  }).format(d);
}

export function formatDateTime(
  iso: string | null | undefined,
  locale: LocaleCode,
  timeZone?: string,
): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    ...(timeZone ? { timeZone } : {}),
  }).format(new Date(iso));
}

export const formatNumber = (n: number, locale: LocaleCode): string =>
  new Intl.NumberFormat(locale).format(n);

export const formatMoney = (cents: number, locale: LocaleCode, currency = "EUR"): string =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).format(cents / 100);

export function formatBytes(bytes: number | null | undefined, locale: LocaleCode): string {
  if (bytes === null || bytes === undefined) return "—";
  if (bytes < 1024) return `${formatNumber(bytes, locale)} B`;
  if (bytes < 1024 * 1024)
    return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / 1024)} KB`;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

/** Whole days from `now` until `iso`, never negative. */
export function daysUntil(iso: string | null | undefined, now: Date): number | null {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - now.getTime()) / 86_400_000));
}
